import Alpine from "alpinejs";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const splitTextToChars = (element: HTMLElement) => {
  if (element.dataset.recipeAnimationSplit === "true") {
    return Array.from(
      element.querySelectorAll<HTMLElement>("[data-recipe-animation-char]")
    );
  }

  const chars: HTMLElement[] = [];
  const lines: HTMLElement[] = [];
  let currentLine = document.createElement("span");
  let currentLineInner = document.createElement("span");

  const resetCurrentLine = () => {
    currentLine = document.createElement("span");
    currentLine.className = "recipe-animation__line";
    currentLine.style.display = "block";
    currentLine.style.overflow = "hidden";

    currentLineInner = document.createElement("span");
    currentLineInner.className = "recipe-animation__line-inner";
    currentLineInner.style.display = "inline-block";
    currentLine.appendChild(currentLineInner);
  };

  const pushCurrentLine = () => {
    if (currentLineInner.childNodes.length === 0) {
      return;
    }

    lines.push(currentLine);
    resetCurrentLine();
  };

  resetCurrentLine();

  const splitNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent || "").replace(/[\t\r\n]+/g, "").trim();

      if (!text) {
        return;
      }

      Array.from(text).forEach((char) => {
        const charElement = document.createElement("span");
        charElement.className = "recipe-animation__char";
        charElement.dataset.recipeAnimationChar = "true";
        charElement.style.display = "inline-block";
        charElement.textContent = char === " " ? "\u00A0" : char;
        currentLineInner.appendChild(charElement);
        chars.push(charElement);
      });

      return;
    }

    if (node instanceof HTMLBRElement) {
      if (window.getComputedStyle(node).display !== "none") {
        pushCurrentLine();
      }
      return;
    }

    Array.from(node.childNodes).forEach(splitNode);
  };

  Array.from(element.childNodes).forEach(splitNode);
  pushCurrentLine();
  element.replaceChildren(...lines);
  element.dataset.recipeAnimationSplit = "true";

  return chars;
};

const getRefElements = (
  refs: Record<string, unknown>,
  names: string[]
): HTMLElement[] =>
  names
    .filter((name) => refs[name] instanceof HTMLElement)
    .map((name) => refs[name] as HTMLElement);

const playFrameLoop = (frames: HTMLElement[]) => {
  if (frames.length < 2) {
    return;
  }

  const timeline = gsap.timeline({ repeat: -1 });

  frames.forEach((frame, index) => {
    timeline.to(
      {},
      {
        duration: index === 2 ? 1 : 0.5,
        ease: "steps(1)",
        onStart: () => {
          gsap.set(frames, { opacity: 0 });
          gsap.set(frame, { opacity: 1 });
        },
      }
    );
  });
};

export const initRecipeAnimation = () => {
  Alpine.data("recipeAnimation", () => {
    return {
      init() {
        const root = this.$el as HTMLElement;
        const refs = this.$refs as Record<string, unknown>;
        const balloon = this.$refs.recipeBalloon as HTMLElement | undefined;
        const balloonText = this.$refs.recipeBalloonText as
          | HTMLElement
          | undefined;
        const image = this.$refs.recipeBalloonImage as HTMLElement | undefined;
        const accent = this.$refs.recipeBalloonAccent as HTMLElement | undefined;

        const textChars = balloonText ? splitTextToChars(balloonText) : [];
        const imageFrames = getRefElements(refs, [
          "recipeBalloonImageFrame1",
          "recipeBalloonImageFrame2",
          "recipeBalloonImageFrame3",
        ]);
        const balloonMotionTargets = [balloon].filter(
          (target): target is HTMLElement => target instanceof HTMLElement
        );
        const fadeTargets = [image, accent].filter(
          (target): target is HTMLElement => target instanceof HTMLElement
        );
        const startImageFrameLoop = () => {
          if (imageFrames[0]) {
            gsap.set(imageFrames[0], { opacity: 1 });
          }
          playFrameLoop(imageFrames);
        };
        const prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;

        gsap.set(balloonMotionTargets, {
          opacity: 0,
          scale: 0.94,
          transformOrigin: "center right",
        });
        gsap.set(fadeTargets, {
          opacity: 0,
        });
        gsap.set(textChars, {
          opacity: 0,
          yPercent: 80,
          transformOrigin: "50% 100%",
        });
        gsap.set(imageFrames, { opacity: 0 });
        if (imageFrames[0]) {
          gsap.set(imageFrames[0], { opacity: 1 });
        }

        if (prefersReducedMotion) {
          gsap.set([...balloonMotionTargets, ...fadeTargets], {
            opacity: 1,
            xPercent: 0,
            yPercent: 0,
            scale: 1,
          });
          gsap.set(textChars, {
            opacity: 1,
            yPercent: 0,
          });
          return;
        }

        const timeline = gsap.timeline({
          scrollTrigger: {
            trigger: root,
            start: "top 75%",
            once: true,
          },
        });

        timeline
          .to(
            balloonMotionTargets,
            {
              opacity: 1,
              duration: 0.7,
              ease: "none",
            },
            "0.3"
          )
          .to(
            balloonMotionTargets,
            {
              scale: 1,
              duration: 0.4,
              ease: "back.out(1.7)",
            },
            "0.3"
          )
          .to(
            fadeTargets,
            {
              opacity: 1,
              duration: 0.35,
              ease: "power2.out",
            },
            "0.3"
          )
          .call(startImageFrameLoop, undefined, "1.1")
          .to(
            textChars,
            {
              opacity: 1,
              yPercent: 0,
              duration: 0.7,
              ease: "back.out(1.45)",
              stagger: 0.03,
            },
            "0.5"
          );
      },
    };
  });
};
